import { supabase } from "./supabase";

/** Max stored file size after compression (fits Free-tier storage guidance). */
export const WEB_IMAGE_MAX_BYTES = 512 * 1024;

/** Longest edge in pixels before we scale down (reduces egress and storage). */
export const WEB_IMAGE_MAX_EDGE_PX = 1920;

function drawScaledToCanvas(
  bitmap: ImageBitmap,
  maxEdge: number,
  sizeFactor: number
): HTMLCanvasElement {
  const bw = bitmap.width;
  const bh = bitmap.height;
  const fit = Math.min(1, maxEdge / Math.max(bw, bh));
  const w = Math.max(1, Math.round(bw * fit * sizeFactor));
  const h = Math.max(1, Math.round(bh * fit * sizeFactor));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available in this browser.");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

async function encodeUnderByteBudget(
  canvas: HTMLCanvasElement
): Promise<Blob> {
  // Prefer JPEG for broad social-preview compatibility (WhatsApp in particular can be flaky with WebP).
  const types: ("image/webp" | "image/jpeg")[] = ["image/jpeg", "image/webp"];
  for (const mime of types) {
    let q = 0.9;
    while (q >= 0.52) {
      const blob = await canvasToBlob(canvas, mime, q);
      if (blob && blob.size <= WEB_IMAGE_MAX_BYTES) return blob;
      q -= 0.06;
    }
  }
  throw new Error("Could not compress image under the size limit.");
}

/**
 * Resizes and re-encodes raster images for web storage.
 * SVG and small GIFs pass through when already within limits.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image files can be uploaded.");
  }

  if (file.type === "image/svg+xml") {
    if (file.size > WEB_IMAGE_MAX_BYTES) {
      throw new Error(
        `SVG must be at most ${Math.round(WEB_IMAGE_MAX_BYTES / 1024)} KB.`
      );
    }
    return file;
  }

  if (file.type === "image/gif" && file.size <= WEB_IMAGE_MAX_BYTES) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(
      "Could not read this image. Try JPEG, PNG, WebP, or a smaller file."
    );
  }

  try {
    let sizeFactor = 1;
    const minEdge = 360;

    while (sizeFactor > 0.12) {
      const canvas = drawScaledToCanvas(
        bitmap,
        WEB_IMAGE_MAX_EDGE_PX,
        sizeFactor
      );
      if (Math.min(canvas.width, canvas.height) < minEdge && sizeFactor < 1) {
        break;
      }
      try {
        const blob = await encodeUnderByteBudget(canvas);
        const ext = blob.type === "image/webp" ? "webp" : "jpg";
        const base = file.name.replace(/\.[^.]+$/, "") || "image";
        return new File([blob], `${base}.${ext}`, { type: blob.type });
      } catch {
        sizeFactor *= 0.88;
      }
    }
    throw new Error(
      "Image is still too large after compression. Try a simpler photo or lower resolution."
    );
  } finally {
    bitmap.close();
  }
}

export async function uploadPublicImage(
  file: File,
  folder: string
): Promise<string> {
  const ready = await prepareImageForUpload(file);
  const ext = ready.name.split(".").pop() || "bin";
  const path = `${folder}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("uploads").upload(path, ready, {
    upsert: true,
    contentType: ready.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("uploads").getPublicUrl(path);
  return data.publicUrl;
}
